import { Controller } from "@hotwired/stimulus"

// Listens for Active Storage Direct Upload events on the form and shows
// per-input progress bars + disables the submit button during uploads.
//
// One Save per page (DPK_Album_Double_Save_Plan.md, owner directive
// 2026-10-08). A Save carrying a newly chosen file was already one Save:
// Active Storage cancels the first submit to upload, this controller
// disables the button in that same instant, and Active Storage clicks the
// button once itself when the uploads end. A Save with NO new file (text
// edits, removals, and since v383 the second Save after a refused one) had
// no guard at all, and a double click posted the form twice: two albums,
// or an error page after a removal. Two listeners close that:
//   - the MARKER on this form (bubble phase, so after Active Storage's
//     document capture listener) marks the form once a submit really goes
//     to the server, i.e. one Active Storage did not cancel to upload first;
//   - the BLOCKER on the window (capture phase, so BEFORE Active Storage)
//     cancels and stops every later submit of THIS form only, so a late file
//     pick can never start a second upload. Other forms on the page (the
//     Delete album button) are never touched (committee engineering E1).
// Active Storage's own click after the uploads passes the blocker, because
// the canceled first submit never marked the form. A failed upload stays
// retryable for the same reason. The page coming back re-enables the
// button: a refused Save is a fresh page, and a back-forward cache restore
// fires pageshow with persisted true.
export default class extends Controller {
  static targets = ["submitBtn", "stillSaving"]
  // About 10 seconds at "Saving..." before "Still saving. Please keep this
  // page open." shows (committee design D4).
  static values = { stillSavingAfter: { type: Number, default: 10000 } }

  connect() {
    this.totalUploads = 0
    this.completedUploads = 0
    this.hasErrors = false
    this.submitted = false
    this.originalBtnValue = this.hasSubmitBtnTarget ? this.submitBtnTarget.value : null

    this._onBlock = this._blockSecondSubmit.bind(this)
    this._onMark = this._markSubmitted.bind(this)
    this._onPageshow = this._pageRestored.bind(this)
    window.addEventListener("submit", this._onBlock, true)
    this.element.addEventListener("submit", this._onMark)
    window.addEventListener("pageshow", this._onPageshow)

    this._onInit = this._handleInit.bind(this)
    this._onStart = this._handleStart.bind(this)
    this._onProgress = this._handleProgress.bind(this)
    this._onEnd = this._handleEnd.bind(this)
    this._onError = this._handleError.bind(this)

    this.element.addEventListener("direct-upload:initialize", this._onInit)
    this.element.addEventListener("direct-upload:start", this._onStart)
    this.element.addEventListener("direct-upload:progress", this._onProgress)
    this.element.addEventListener("direct-upload:end", this._onEnd)
    this.element.addEventListener("direct-upload:error", this._onError)
  }

  disconnect() {
    window.removeEventListener("submit", this._onBlock, true)
    this.element.removeEventListener("submit", this._onMark)
    window.removeEventListener("pageshow", this._onPageshow)
    clearTimeout(this.stillSavingTimer)
    this.element.removeEventListener("direct-upload:initialize", this._onInit)
    this.element.removeEventListener("direct-upload:start", this._onStart)
    this.element.removeEventListener("direct-upload:progress", this._onProgress)
    this.element.removeEventListener("direct-upload:end", this._onEnd)
    this.element.removeEventListener("direct-upload:error", this._onError)
  }

  // The blocker: once a submit of this form has gone to the server, every
  // later one is canceled before anything (Active Storage included) sees it.
  _blockSecondSubmit(event) {
    if (event.target !== this.element || !this.submitted) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  // The marker: a submit nobody canceled is going to the server.
  _markSubmitted(event) {
    if (event.defaultPrevented) return
    this.submitted = true
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.value = "Saving..."
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    clearTimeout(this.stillSavingTimer)
    this.stillSavingTimer = setTimeout(() => {
      if (this.hasStillSavingTarget) this.stillSavingTarget.textContent = this.stillSavingTarget.dataset.text
    }, this.stillSavingAfterValue)
  }

  // Back to this page from the back-forward cache: it can be saved again.
  _pageRestored(event) {
    if (!event.persisted) return
    this.submitted = false
    clearTimeout(this.stillSavingTimer)
    if (this.hasStillSavingTarget) this.stillSavingTarget.textContent = ""
    this.totalUploads = 0
    this.completedUploads = 0
    this.hasErrors = false
    this._resetButton()
  }

  _handleInit(event) {
    this.totalUploads++

    if (this.hasSubmitBtnTarget && !this.originalBtnValue) {
      this.originalBtnValue = this.submitBtnTarget.value
    }

    const progressEl = this._findProgressBar(event.target)
    if (progressEl) {
      progressEl.classList.remove("hidden")
      const bar = progressEl.querySelector("[data-progress-bar]")
      const text = progressEl.querySelector("[data-progress-text]")
      if (bar) { bar.style.width = "0%"; bar.className = bar.className.replace(/bg-\[.*?\]/g, "") + " bg-[var(--color-mc-accent-terracotta)]" }
      if (text) { text.textContent = "Waiting..."; text.className = "text-xs text-[var(--color-mc-text-secondary)] w-16 text-right" }
    }

    this._updateSubmitButton()
  }

  _handleStart(event) {
    const progressEl = this._findProgressBar(event.target)
    if (progressEl) {
      const text = progressEl.querySelector("[data-progress-text]")
      if (text) text.textContent = "0%"
    }
  }

  _handleProgress(event) {
    const { progress } = event.detail
    const progressEl = this._findProgressBar(event.target)
    if (progressEl) {
      const bar = progressEl.querySelector("[data-progress-bar]")
      const text = progressEl.querySelector("[data-progress-text]")
      if (bar) bar.style.width = `${progress}%`
      if (text) text.textContent = `${Math.round(progress)}%`
    }
  }

  _handleEnd(event) {
    this.completedUploads++

    const progressEl = this._findProgressBar(event.target)
    if (progressEl) {
      const bar = progressEl.querySelector("[data-progress-bar]")
      const text = progressEl.querySelector("[data-progress-text]")
      if (bar) {
        bar.style.width = "100%"
        bar.className = bar.className.replace(/bg-\[.*?\]/g, "") + " bg-[var(--color-status-confirmed)]"
      }
      if (text) {
        text.textContent = "Uploaded"
        text.className = "text-xs text-[var(--color-status-confirmed)] w-16 text-right"
      }
    }

    this._updateSubmitButton()

    if (this.completedUploads >= this.totalUploads && this.hasSubmitBtnTarget) {
      this.submitBtnTarget.value = "Saving..."
    }
  }

  _handleError(event) {
    this.completedUploads++
    this.hasErrors = true
    event.preventDefault()

    const progressEl = this._findProgressBar(event.target)
    if (progressEl) {
      const bar = progressEl.querySelector("[data-progress-bar]")
      const text = progressEl.querySelector("[data-progress-text]")
      if (bar) bar.className = bar.className.replace(/bg-\[.*?\]/g, "") + " bg-[#E57373]"
      if (text) {
        text.textContent = "Failed"
        text.className = "text-xs text-[#E57373] w-16 text-right"
      }
    }

    if (this.completedUploads >= this.totalUploads) {
      this._resetButton()
      this.totalUploads = 0
      this.completedUploads = 0
      this.hasErrors = false
    }
  }

  _findProgressBar(input) {
    return input.parentElement?.querySelector(".upload-progress") ||
           input.closest(".track-fields")?.querySelector(".upload-progress")
  }

  _updateSubmitButton() {
    if (!this.hasSubmitBtnTarget) return

    if (this.completedUploads < this.totalUploads) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.value = `Uploading file ${this.completedUploads + 1} of ${this.totalUploads}...`
      this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  _resetButton() {
    if (!this.hasSubmitBtnTarget) return
    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.value = this.originalBtnValue
    this.submitBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
  }
}
